import React, { useState, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import {
    calculateSCurve, calculateProjectProgress, truncateProgress
} from '../src/utils/planningUtils';
import {
    Task, BudgetFolder, BudgetItem, ProjectProgressSnapshot, PhysicalUnit, Level,
    ProtocolState, ProtocolConfig, Contract, PaymentStatement, WeeklyPlan,
    Responsible, ProjectInfo, LookaheadPlan, CNCGroup, ConstraintGroup, Subcontractor
} from '../src/types';

interface ReportsProps {
    // Budget
    budgetItems: BudgetItem[];
    // Planning
    tasks: Task[];
    weeklyPlans: WeeklyPlan[];
    lookaheadPlans: LookaheadPlan[];
    // Quality
    savedProtocols: Record<string, ProtocolState>;
    // Subcontracts
    contracts: Contract[];
    paymentStatements: PaymentStatement[];
    subcontractors: Subcontractor[];
    // Shared Data
    responsibles: Responsible[];
    cncGroups: CNCGroup[];
    constraintGroups: ConstraintGroup[];
}

const COLORS = ['#0ea5e9', '#38bdf8', '#7dd3fc', '#0284c7', '#0369a1', '#bae6fd', '#f0f9ff', '#64748b', '#94a3b8'];

export default function ReportsScreen({
    budgetItems,
    tasks,
    weeklyPlans,
    lookaheadPlans,
    savedProtocols,
    contracts,
    paymentStatements,
    subcontractors,
    responsibles,
    cncGroups,
    constraintGroups,
    units,
    matrixData,
    protocolConfigs,
    projectInfo,
    progressHistory
}: ReportsProps & {
    units: PhysicalUnit[],
    matrixData: Record<string, any>,
    protocolConfigs: Record<string, ProtocolConfig>,
    projectInfo: ProjectInfo,
    progressHistory: ProjectProgressSnapshot[]
}) {
    const [activeTab, setActiveTab] = useState<'budget' | 'planning' | 'quality' | 'subcontracts'>('planning');
    const [budgetSubTab, setBudgetSubTab] = useState<'resumen_ecg' | 'comparativo' | 'inversiones' | 'avance'>('resumen_ecg');
    const [planningSubTab, setPlanningSubTab] = useState<'master' | 'intermediate' | 'weekly'>('master');
    const [subcontractsSubTab, setSubcontractsSubTab] = useState<'contratos' | 'estados_pago'>('contratos');
    const [subcontractFilter, setSubcontractFilter] = useState('');

    // Date Filters
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]);

    // Quality Filters
    const [qualityFilterResponsible, setQualityFilterResponsible] = useState("");
    const [qualityFilterTask, setQualityFilterTask] = useState("");

    // --- Helpers ---
    const filterByDate = (dateStr: string) => {
        if (!dateStr) return false;
        return dateStr >= startDate && dateStr <= endDate;
    };

    const getResponsibleName = (id: string) => responsibles.find(r => r.id === id)?.name || 'Sin Asignar';
    const getCNCName = (id: string) => {
        for (const group of cncGroups) {
            const cause = group.causes.find(c => c.id === id);
            if (cause) return cause.name;
        }
        return 'Desconocido';
    };

    // --- Data Processing (Mock/Simulated for now based on props) ---

    // 1. Budget Stats
    const budgetStats = useMemo(() => {
        const totalItems = budgetItems.length;
        const totalAmount = budgetItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
        return { totalItems, totalAmount };
    }, [budgetItems]);

    // 2. Planning Stats
    const planningStats = useMemo(() => {
        // Master: Use synchronized progress history
        let realProgress = 0;
        if (progressHistory && progressHistory.length > 0) {
            let validHistory = progressHistory.filter(h => h && h.date);
            const todayStr = new Date().toISOString().split('T')[0];
            
            validHistory = [...validHistory].sort((a, b) => a.date.localeCompare(b.date));
            while (validHistory.length > 0 && validHistory[validHistory.length - 1].progress === 0 && validHistory[validHistory.length - 1].date !== todayStr) {
                validHistory.pop();
            }

            if (validHistory.length > 0) {
                realProgress = validHistory[validHistory.length - 1].progress || 0;
            }
        }

        const virtualHistory = [...(progressHistory || [])];
        const todayStr = new Date().toISOString().split('T')[0];
        
        while (virtualHistory.length > 0 && virtualHistory[virtualHistory.length - 1].progress === 0 && virtualHistory[virtualHistory.length - 1].date !== todayStr) {
            virtualHistory.pop();
        }

        const sCurveData = calculateSCurve(tasks, virtualHistory);
        const lastPoint = sCurveData[sCurveData.length - 1];
        const scheduledProgress = lastPoint ? lastPoint.plan : 0;

        const completedTasks = tasks.filter(t => t.progress === 100).length;
        const totalTasks = tasks.length;
        const progressAvg = tasks.length > 0 ? tasks.reduce((sum, t) => sum + (t.progress || 0), 0) / tasks.length : 0;

        // --- Weekly Calculation ---
        const allAllocations = weeklyPlans
            // Filter by date range if needed, for now taking all or could filter by plan dates
            .flatMap(p => p.allocations);

        const totalAllocations = allAllocations.length;
        const executedAllocations = allAllocations.filter(a => a.executed).length;
        const failedAllocations = totalAllocations - executedAllocations;
        const globalPPC = totalAllocations > 0 ? (executedAllocations / totalAllocations) * 100 : 0;

        // CNC Stats
        const cncCounts: Record<string, number> = {};
        allAllocations.filter(a => !a.executed && a.cncId).forEach(a => {
            const name = getCNCName(a.cncId!);
            cncCounts[name] = (cncCounts[name] || 0) + 1;
        });
        const cncData = Object.entries(cncCounts).map(([name, value]) => ({
            name,
            value,
            percentage: truncateProgress((value / failedAllocations) * 100)
        }));

        // Responsible Stats
        const respStatsMap: Record<string, { assigned: number, executed: number }> = {};
        allAllocations.forEach(a => {
            if (!respStatsMap[a.responsibleId]) respStatsMap[a.responsibleId] = { assigned: 0, executed: 0 };
            respStatsMap[a.responsibleId].assigned++;
            if (a.executed) respStatsMap[a.responsibleId].executed++;
        });
        const responsibleData = Object.entries(respStatsMap).map(([id, stats]) => ({
            name: getResponsibleName(id),
            assigned: stats.assigned,
            executed: stats.executed,
            failed: stats.assigned - stats.executed,
            compliance: stats.assigned > 0 ? truncateProgress((stats.executed / stats.assigned) * 100) : 0
        }));

        // PPC History Graph
        const ppcData = weeklyPlans.map(plan => {
            const total = plan.allocations.length;
            const executed = plan.allocations.filter(a => a.executed).length;
            return {
                name: `Semana ${plan.id}`,
                ppc: total > 0 ? truncateProgress((executed / total) * 100) : 0,
                planned: total,
                executed: executed
            };
        });

        // Intermediate: Constraints
        const allConstraints = lookaheadPlans.flatMap(p => p.constraints);

        // Stats for cards
        const constraintStats = {
            open: allConstraints.filter(c => c.status === 'Pendiente').length,
            closed: allConstraints.filter(c => c.status === 'Liberado').length,
            total: allConstraints.length
        };

        // By Type (Chart)
        const constraintTypeCounts: Record<string, number> = {};
        allConstraints.forEach(c => {
            let name = 'Desconocido';
            for (const g of constraintGroups) {
                for (const d of g.definitions) {
                    if (d.id === c.typeId) {
                        name = d.name;
                        break;
                    }
                }
                if (name !== 'Desconocido') break;
            }
            constraintTypeCounts[name] = (constraintTypeCounts[name] || 0) + 1;
        });
        const constraintTypeData = Object.entries(constraintTypeCounts).map(([name, value]) => ({ name, value }));

        // By Responsible (Table)
        const constraintRespMap: Record<string, { assigned: number, closed: number, pending: number }> = {};
        allConstraints.forEach(c => {
            if (!constraintRespMap[c.responsibleId]) constraintRespMap[c.responsibleId] = { assigned: 0, closed: 0, pending: 0 };
            constraintRespMap[c.responsibleId].assigned++;
            if (c.status === 'Liberado') {
                constraintRespMap[c.responsibleId].closed++;
            } else {
                constraintRespMap[c.responsibleId].pending++;
            }
        });
        const constraintResponsibleData = Object.entries(constraintRespMap).map(([id, stats]) => ({
            name: getResponsibleName(id),
            assigned: stats.assigned,
            closed: stats.closed,
            pending: stats.pending,
            compliance: stats.assigned > 0 ? truncateProgress((stats.closed / stats.assigned) * 100) : 0
        }));

        return {
            progressAvg, ppcData,
            weekly: {
                totalAllocations,
                executedAllocations,
                failedAllocations,
                globalPPC,
                cncData,
                responsibleData
            },
            intermediate: {
                constraintTypeData,
                constraintResponsibleData,
                constraintStats
            },
            master: {
                realProgress,
                scheduledProgress,
                completedTasks,
                totalTasks,
                sCurveData
            }
        };
    }, [
        tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, progressHistory,
        cncGroups, constraintGroups, lookaheadPlans, responsibles, budgetItems
    ]);

    // 3. Quality Stats
    const qualityStats = useMemo(() => {
        let protocols = Object.values(savedProtocols);

        // Build Map: Protocol -> Responsible (from WeeklyAllocations)
        const protocolResponsibleMap: Record<string, string> = {};
        weeklyPlans.forEach(plan => {
            plan.allocations.forEach(alloc => {
                const key = `${alloc.taskId}_${alloc.unitId}`;
                protocolResponsibleMap[key] = alloc.responsibleId;
            });
        });

        // Filter by Partida (Task)
        if (qualityFilterTask) {
            protocols = protocols.filter(p => p.taskId === qualityFilterTask);
        }

        // Filter by Responsible for the PIE CHART and CARDS
        if (qualityFilterResponsible) {
            protocols = protocols.filter(p => {
                const key = `${p.taskId}_${p.unitId}`;
                const respId = protocolResponsibleMap[key];
                return respId === qualityFilterResponsible;
            });
        }

        const total = protocols.length;
        const open = protocols.filter(p => p.status === 'Abierto').length;
        const closed = protocols.filter(p => p.status === 'Cerrado').length;
        const rejected = protocols.filter(p => p.status === 'Rechazado').length;

        const data = [
            { name: 'Abierto', value: open },
            { name: 'Cerrado', value: closed },
            { name: 'Rechazado', value: rejected },
        ];

        // Compliance Table Data
        const statsByResp: Record<string, { assigned: number, closed: number, open: number }> = {};
        protocols.forEach(p => {
            const key = `${p.taskId}_${p.unitId}`;
            const respId = protocolResponsibleMap[key] || 'unknown';

            if (!statsByResp[respId]) statsByResp[respId] = { assigned: 0, closed: 0, open: 0 };

            statsByResp[respId].assigned++;
            if (p.status === 'Cerrado') {
                statsByResp[respId].closed++;
            } else {
                statsByResp[respId].open++;
            }
        });

        const complianceData = Object.entries(statsByResp).map(([id, stats]) => ({
            name: id === 'unknown' ? 'Sin Asignar' : getResponsibleName(id),
            assigned: stats.assigned,
            closed: stats.closed,
            open: stats.open,
            compliance: stats.assigned > 0 ? truncateProgress((stats.closed / stats.assigned) * 100) : 0
        }));

        return { total, data, complianceData };
    }, [savedProtocols, weeklyPlans, qualityFilterResponsible, qualityFilterTask, responsibles]);

    // 4. Subcontracts Stats
    const subcontractStats = useMemo(() => {
        // --- Mock Data if empty ---
        let useContracts = contracts;
        let useSubcontractors = subcontractors;
        let useEPs = paymentStatements;

        if (contracts.length === 0) {
            useSubcontractors = [
                { id: 's1', rut: '76.123.456-7', razonSocial: 'Constructora Civil SpA', giro: 'Construcción', representante: 'Juan Pérez', telefono: '+56911111111', correo: 'contacto@civil.cl' },
                { id: 's2', rut: '77.222.333-4', razonSocial: 'Elecricidad Total Ltda', giro: 'Instalaciones Eléctricas', representante: 'Maria Gonzalez', telefono: '+56922222222', correo: 'mg@electricidad.cl' },
                { id: 's3', rut: '78.333.444-5', razonSocial: 'Climatización Expertos', giro: 'Climatización', representante: 'Pedro Tapia', telefono: '+56933333333', correo: 'pt@clima.cl' }
            ];
            useContracts = [
                { id: 'c1', subcontractorId: 's1', startDate: '2026-01-10', endDate: '2026-06-30', retention: 5, items: [], status: 'Activo', totalAmount: 150000000 },
                { id: 'c2', subcontractorId: 's2', startDate: '2026-02-01', endDate: '2026-04-15', retention: 5, items: [], status: 'Activo', totalAmount: 45000000 },
                { id: 'c3', subcontractorId: 's3', startDate: '2026-03-01', endDate: '2026-08-30', retention: 10, items: [], status: 'Borrador', totalAmount: 80000000 }
            ];
            useEPs = [
                { id: 'ep1', contractId: 'c1', subcontractorId: 's1', period: 'Enero 2026', progress: 10, amount: '15.000.000', status: 'Aprobado', items: [] },
                { id: 'ep2', contractId: 'c1', subcontractorId: 's1', period: 'Febrero 2026', progress: 25, amount: '22.500.000', status: 'Aprobado', items: [] },
                { id: 'ep3', contractId: 'c2', subcontractorId: 's2', period: 'Febrero 2026', progress: 50, amount: '22.500.000', status: 'Enviado', items: [] }
            ];
        }

        const totalContracts = useContracts.length;
        const activeContracts = useContracts.filter(c => c.status === 'Activo').length;
        const totalPaid = useEPs
            .filter(ep => ep.status === 'Aprobado' || ep.status === 'Autorizado')
            .reduce((sum, ep) => {
                const val = parseFloat(ep.amount.replace(/[^0-9.-]+/g, ""));
                return sum + (isNaN(val) ? 0 : val);
            }, 0);

        // --- Contratos Breakdown ---
        const contractsBreakdown = useContracts.map(c => {
            const sub = useSubcontractors.find(s => s.id === c.subcontractorId);
            return {
                id: c.id,
                subcontractor: sub?.razonSocial || 'Desconocido',
                startDate: c.startDate || 'N/A',
                endDate: c.endDate || 'N/A',
                amount: c.totalAmount,
                status: c.status,
                attachments: Math.floor(Math.random() * 5) // Mock attachments count
            };
        });

        const contractsPieData = contractsBreakdown
            .reduce((acc, curr) => {
                const existing = acc.find(item => item.name === curr.subcontractor);
                if (existing) {
                    existing.value += curr.amount;
                } else {
                    acc.push({ name: curr.subcontractor, value: curr.amount });
                }
                return acc;
            }, [] as { name: string, value: number }[]);

        // --- Estados de Pago Breakdown ---
        const epBreakdown = useSubcontractors.map(s => {
            // Find all contracts for this subcontractor
            const subContracts = useContracts.filter(c => c.subcontractorId === s.id);
            const contractIds = subContracts.map(c => c.id);

            // Find all EPs for these contracts
            const eps = useEPs.filter(ep => contractIds.includes(ep.contractId));

            const totalEPHere = eps.length;
            const paidHere = eps.reduce((sum, ep) => {
                const val = parseFloat(ep.amount.replace(/[^0-9.-]+/g, ""));
                return sum + (isNaN(val) ? 0 : val);
            }, 0);

            return {
                id: s.id,
                name: s.razonSocial,
                totalEPs: totalEPHere,
                totalPaid: paidHere,
                eps: eps.map(ep => ({
                    period: ep.period,
                    amount: parseFloat(ep.amount.replace(/[^0-9.-]+/g, "") || '0'),
                    status: ep.status
                }))
            };
        }).filter(item => item.totalEPs > 0); // Only showing active ones? Maybe all. User said "filtre por subcontratos".

        return {
            totalContracts, activeContracts, totalPaid,
            contractsBreakdown, contractsPieData, epBreakdown
        };
    }, [contracts, paymentStatements, subcontractors]);


    // --- Components ---

    const DateFilter = () => (
        <div className="flex items-center gap-4 bg-white dark:bg-slate-800 p-3 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Desde:</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
            </div>
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Hasta:</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
            </div>
        </div>
    );

    const StatCard = ({ title, value, icon, color = "bg-primary" }: { title: string, value: string | number, icon: string, color?: string }) => (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-start justify-between">
            <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{title}</p>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{value}</h3>
            </div>
            <div className={`p-3 rounded-xl ${color} bg-opacity-10 text-${color.replace('bg-', '')}`}>
                <span className="material-symbols-outlined">{icon}</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
            {/* Header */}
            <header className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-8 py-5 flex items-center justify-between z-10">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Informes</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Dashboard de Control de Proyecto</p>
                </div>
                <DateFilter />
            </header>

            {/* Tabs & Content */}
            <div className="flex-1 overflow-y-auto p-8">
                {/* Main Tabs */}
                <div className="flex gap-2 mb-8 bg-white dark:bg-slate-800 p-1 rounded-xl w-fit shadow-sm border border-slate-200 dark:border-slate-700">
                    {[
                        // { id: 'budget', label: 'Presupuesto', icon: 'attach_money' },
                        { id: 'planning', label: 'Planificación', icon: 'calendar_month' },
                        { id: 'quality', label: 'Calidad', icon: 'fact_check' },
                        { id: 'subcontracts', label: 'Subcontratos', icon: 'handshake' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                ? 'bg-primary text-white shadow-md'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                        >
                            <span className="material-symbols-outlined text-[20px]">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* --- BUDGET VIEW --- */}
                {activeTab === 'budget' && (
                    <div className="animate-fade-in space-y-6">
                        {/* Budget Sub-Tabs */}
                        <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
                            {[
                                { id: 'resumen_ecg', label: 'Resumen ECG' },
                                { id: 'comparativo', label: 'Comparativo Presupuestos' },
                                { id: 'inversiones', label: 'Resumen de Inversiones' },
                                { id: 'avance', label: 'Detalle de Avance' }
                            ].map(sub => (
                                <button
                                    key={sub.id}
                                    onClick={() => setBudgetSubTab(sub.id as any)}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${budgetSubTab === sub.id
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                        }`}
                                >
                                    {sub.label}
                                </button>
                            ))}
                        </div>

                        {/* 1. Resumen ECG */}
                        {budgetSubTab === 'resumen_ecg' && (
                            <div className="space-y-6">
                                {/* Top Table: Gestión de Proyecto UF/M2 */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Gestión de Proyecto UF/M2 (Simulado)</h3>
                                    <table className="w-full text-sm text-center text-slate-600 dark:text-slate-400">
                                        <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                            <tr>
                                                <th className="px-4 py-2">Ppto con Utilidad</th>
                                                <th className="px-4 py-2">Ppto V.0 "Sin Utilidad y sin Of. Central"</th>
                                                <th className="px-4 py-2">Ppto V.1 "Inicial Obra"</th>
                                                <th className="px-4 py-2">P.O.M "Ppto Modificado Obra"</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="bg-white dark:bg-slate-800">
                                                <td className="px-4 py-3 font-medium">31,74</td>
                                                <td className="px-4 py-3">28,23</td>
                                                <td className="px-4 py-3">25,56</td>
                                                <td className="px-4 py-3">23,97</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Main Table: Presupuesto */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Presupuesto (Simulado)</h3>
                                    <table className="w-full text-sm text-right text-slate-600 dark:text-slate-400">
                                        <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                            <tr>
                                                <th className="px-4 py-2 text-left">Nombre Grupo</th>
                                                <th className="px-4 py-2">Presupuesto V.0</th>
                                                <th className="px-4 py-2">Ajuste Gerencia</th>
                                                <th className="px-4 py-2">Presupuesto V.1</th>
                                                <th className="px-4 py-2">Adicionales</th>
                                                <th className="px-4 py-2">Cierre Obra</th>
                                                <th className="px-4 py-2">P.O.M</th>
                                                <th className="px-4 py-2">Avance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Mock Data Rows */}
                                            {[
                                                { name: "1 - MATERIALES", v0: 1059183004, ajuste: -88045132, v1: 971137872, adic: 0, cierre: -89980664, pom: 881157208, avance: 311564059 },
                                                { name: "2 - SUBCONTRATOS", v0: 1993869981, ajuste: -32253254, v1: 1961616727, adic: 92100795, cierre: -120035686, pom: 1933681836, avance: 328553209 },
                                                { name: "3 - OBRAS COMPLEMENTARIAS", v0: 61314600, ajuste: 27764711, v1: 89079311, adic: 0, cierre: 26920081, pom: 115999392, avance: 101093778 },
                                                { name: "4 - GASTOS GENERALES", v0: 530430872, ajuste: 38833930, v1: 569264802, adic: 0, cierre: 157605633, pom: 726870435, avance: 214955266 },
                                                { name: "5 - HONORARIOS", v0: 707200, ajuste: 1131871, v1: 1839071, adic: 0, cierre: 12437983, pom: 14277054, avance: 14373970 },
                                                { name: "6 - URBANIZACIONES", v0: 1431616159, ajuste: -299727254, v1: 1131888905, adic: 0, cierre: -322442732, pom: 809446172, avance: 144054729 },
                                                { name: "7 - VALORES PROFORMA", v0: 163511533, ajuste: 0, v1: 163511533, adic: 0, cierre: -29271640, pom: 134239893, avance: 0 },
                                                { name: "8 - MANO DE OBRA", v0: 1077511310, ajuste: -247208965, v1: 830302345, adic: 0, cierre: -178061205, pom: 652241140, avance: 251059695 },
                                                { name: "9 - MENSAJES Y UTILIDAD", v0: 0, ajuste: 352000, v1: 352000, adic: 0, cierre: 18960359, pom: 19312359, avance: 235092437 },
                                            ].map((row, idx) => (
                                                <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                                                    <td className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">{row.name}</td>
                                                    <td className="px-4 py-3">{row.v0.toLocaleString()}</td>
                                                    <td className={`px-4 py-3 ${row.ajuste < 0 ? 'text-red-500' : 'text-slate-600 dark:text-slate-400'}`}>{row.ajuste.toLocaleString()}</td>
                                                    <td className="px-4 py-3 font-semibold">{row.v1.toLocaleString()}</td>
                                                    <td className="px-4 py-3">{row.adic > 0 ? row.adic.toLocaleString() : ''}</td>
                                                    <td className={`px-4 py-3 ${row.cierre < 0 ? 'text-red-500' : 'text-slate-600 dark:text-slate-400'}`}>{row.cierre.toLocaleString()}</td>
                                                    <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">{row.pom.toLocaleString()}</td>
                                                    <td className="px-4 py-3">{row.avance.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-primary text-white font-bold">
                                            <tr>
                                                <td className="px-4 py-3 text-left">Total</td>
                                                <td className="px-4 py-3">6.318.144.659</td>
                                                <td className="px-4 py-3">-599.152.094</td>
                                                <td className="px-4 py-3">5.718.992.566</td>
                                                <td className="px-4 py-3">92.100.795</td>
                                                <td className="px-4 py-3">-523.867.871</td>
                                                <td className="px-4 py-3">5.287.225.489</td>
                                                <td className="px-4 py-3">1.600.747.142</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* 2. Comparativo Presupuestos */}
                        {budgetSubTab === 'comparativo' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Top Left Table: Presupuesto Summary */}
                                    <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Presupuesto</h3>
                                        <table className="w-full text-sm text-right text-slate-600 dark:text-slate-400">
                                            <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                                <tr>
                                                    <th className="px-4 py-2 text-left">Nombre Grupo</th>
                                                    <th className="px-4 py-2">Presupuesto V.1</th>
                                                    <th className="px-4 py-2">Adicionales</th>
                                                    <th className="px-4 py-2">Cierre Obra</th>
                                                    <th className="px-4 py-2">P.O.M</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    { name: "1 - MATERIALES", v1: 971137872, adic: 0, cierre: -89980664, pom: 881157208 },
                                                    { name: "2 - SUBCONTRATOS", v1: 1961616727, adic: 92100795, cierre: -120035686, pom: 1933681836 },
                                                    { name: "3 - OBRAS COMPLEMENTARIAS", v1: 89079311, adic: 0, cierre: 26920081, pom: 115999392 },
                                                    { name: "4 - GASTOS GENERALES", v1: 569264802, adic: 0, cierre: 157605633, pom: 726870435 },
                                                    { name: "5 - HONORARIOS", v1: 1839071, adic: 0, cierre: 12437983, pom: 14277054 },
                                                    { name: "6 - URBANIZACIONES", v1: 1131888905, adic: 0, cierre: -322442732, pom: 809446172 },
                                                    { name: "7 - VALORES PROFORMA", v1: 163511533, adic: 0, cierre: -29271640, pom: 134239893 },
                                                    { name: "8 - MANO DE OBRA", v1: 830302345, adic: 0, cierre: -178061205, pom: 652241140 },
                                                    { name: "9 - MENSAJES Y UTILIDAD", v1: 352000, adic: 0, cierre: 18960359, pom: 19312359 },
                                                ].map((row, idx) => (
                                                    <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                                                        <td className="px-4 py-2 text-left font-medium text-slate-900 dark:text-white">{row.name}</td>
                                                        <td className="px-4 py-2">{row.v1.toLocaleString()}</td>
                                                        <td className="px-4 py-2">{row.adic > 0 ? row.adic.toLocaleString() : ''}</td>
                                                        <td className={`px-4 py-2 ${row.cierre < 0 ? 'text-red-500' : 'text-slate-600 dark:text-slate-400'}`}>{row.cierre.toLocaleString()}</td>
                                                        <td className="px-4 py-2 font-semibold text-slate-900 dark:text-white">{row.pom.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-primary text-white font-bold">
                                                <tr>
                                                    <td className="px-4 py-2 text-left">Total</td>
                                                    <td className="px-4 py-2">5.718.992.566</td>
                                                    <td className="px-4 py-2">92.100.795</td>
                                                    <td className="px-4 py-2">-447.447.107</td>
                                                    <td className="px-4 py-2">5.363.646.253</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {/* Top Right Chart: Propuesta / P.O.M */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Propuesta / P.O.M</h3>
                                        <div className="h-[300px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart layout="vertical" data={[
                                                    { name: 'P.O.M', value: 5364, fill: '#0ea5e9' }, // Blue
                                                    { name: 'Presupuesto V.1', value: 5719, fill: '#94a3b8' }, // Gray
                                                ]}>
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis type="number" hide />
                                                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                                                    <Tooltip formatter={(value) => `${value} mill.`} />
                                                    <Bar dataKey="value" barSize={40} radius={[0, 4, 4, 0]}>
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div className="mt-2 text-center text-sm font-medium text-slate-600 dark:text-slate-400">
                                            93,8%
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Bottom Left Table: Presupuesto V.1 Detail */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Presupuesto V.1</h3>
                                        <table className="w-full text-xs text-right text-slate-600 dark:text-slate-400">
                                            <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                                <tr>
                                                    <th className="px-2 py-1 text-left">Codigo</th>
                                                    <th className="px-2 py-1 text-left">Descripcion</th>
                                                    <th className="px-2 py-1">Cant.</th>
                                                    <th className="px-2 py-1">Unid</th>
                                                    <th className="px-2 py-1">P.U</th>
                                                    <th className="px-2 py-1">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    { code: "110209", desc: "ALAMBRE GALVANIZADO Nº18", cant: 1, unit: "KG", pu: 1126, total: 777 },
                                                    { code: "407401", desc: "AJUSTE", cant: 1, unit: "GL", pu: -55005446, total: -55005446 },
                                                    { code: "601005", desc: "S. VALVULA 50 MM, AGUA POTABLE", cant: 1, unit: "UNIDAD", pu: 52798, total: 52798 },
                                                    { code: "601005", desc: "S. VENTOSA 50 MM, AGUA POTABLE", cant: 1, unit: "UNIDAD", pu: 74052, total: 74052 },
                                                    { code: "300084", desc: "ARRIENDO CASETA DUCHA", cant: 1, unit: "UNIDAD", pu: 90000, total: 90000 },
                                                    { code: "601008", desc: "S. CONEXION ALCANTARILLADO", cant: 1, unit: "GLOBAL", pu: 178000, total: 178000 },
                                                    { code: "900400", desc: "AHORRO GG", cant: 1, unit: "GLOBAL", pu: 352000, total: 352000 },
                                                    { code: "400150", desc: "CONSUMO ARTICULO PRIMEROS AU...", cant: 1, unit: "GLOBAL", pu: 454545, total: 454545 },
                                                    { code: "601005", desc: "S. INSPECCION PARTICULAR AP", cant: 1, unit: "UNIDAD", pu: 507153, total: 507153 },
                                                ].map((row, idx) => (
                                                    <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700">
                                                        <td className="px-2 py-1 text-left">{row.code}</td>
                                                        <td className="px-2 py-1 text-left truncate max-w-[150px]">{row.desc}</td>
                                                        <td className="px-2 py-1">{row.cant}</td>
                                                        <td className="px-2 py-1">{row.unit}</td>
                                                        <td className="px-2 py-1">{row.pu.toLocaleString()}</td>
                                                        <td className={`px-2 py-1 ${row.total < 0 ? 'text-red-500' : ''}`}>{row.total.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-primary text-white font-bold">
                                                <tr>
                                                    <td colSpan={5} className="px-2 py-1 text-left">Total</td>
                                                    <td className="px-2 py-1">5.718.992.566</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {/* Bottom Right Table: P.O.M Detail */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">P.O.M</h3>
                                        <table className="w-full text-xs text-right text-slate-600 dark:text-slate-400">
                                            <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                                <tr>
                                                    <th className="px-2 py-1 text-left">Codigo</th>
                                                    <th className="px-2 py-1 text-left">Descripcion</th>
                                                    <th className="px-2 py-1">Cant.</th>
                                                    <th className="px-2 py-1">Unid</th>
                                                    <th className="px-2 py-1">P.U</th>
                                                    <th className="px-2 py-1">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    { code: "1", desc: "S. DESARME TECHUMBRES", cant: 1, unit: "UN", pu: 250000, total: 250000 },
                                                    { code: "1", desc: "ARRIENDO ESCARIFICADORA", cant: 2, unit: "UN", pu: 1590000, total: 3180000 },
                                                    { code: "1", desc: "CONTENEDORES BASUREROS", cant: 4, unit: "UN", pu: 105000, total: 420000 },
                                                    { code: "1", desc: "PUNTA CORRIENTE 1.1/2", cant: 14, unit: "UN", pu: 3270, total: 45780 },
                                                    { code: "1", desc: "S. RESANE INTERIOR 1 PISO", cant: 21, unit: "UN", pu: 130000, total: 2730000 },
                                                    { code: "1", desc: "S. RESANE INTERIOR 2 PISO", cant: 22, unit: "UN", pu: 130000, total: 2860000 },
                                                    { code: "1", desc: "S. RESANE FACHADA", cant: 22, unit: "UN", pu: 140000, total: 3080000 },
                                                    { code: "1", desc: "S. SOBRE EXCAVACION", cant: 30, unit: "UN", pu: 213610, total: 6408314 },
                                                    { code: "1", desc: "COLAFRIA GRIP BOND", cant: 35, unit: "UN", pu: 10080, total: 352800 },
                                                ].map((row, idx) => (
                                                    <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700">
                                                        <td className="px-2 py-1 text-left">{row.code}</td>
                                                        <td className="px-2 py-1 text-left truncate max-w-[150px]">{row.desc}</td>
                                                        <td className="px-2 py-1">{row.cant}</td>
                                                        <td className="px-2 py-1">{row.unit}</td>
                                                        <td className="px-2 py-1">{row.pu.toLocaleString()}</td>
                                                        <td className="px-2 py-1">{row.total.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-primary text-white font-bold">
                                                <tr>
                                                    <td colSpan={5} className="px-2 py-1 text-left">Total</td>
                                                    <td className="px-2 py-1">5.363.646.253</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 3. Resumen de Inversiones */}
                        {budgetSubTab === 'inversiones' && (
                            <div className="space-y-6">
                                {/* Top Chart: Gastado Neto y Gastado Bruto por Año Mes */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Gastado Neto y Gastado Bruto por Año Mes</h3>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={[
                                            { name: '2025/ago', neto: 2132720, bruto: 2132720 },
                                            { name: '2025/sep', neto: 82509193, bruto: 94959820 },
                                            { name: '2025/oct', neto: 223858439, bruto: 259053156 },
                                            { name: '2025/nov', neto: 210094005, bruto: 238648239 },
                                            { name: '2025/dic', neto: 526368506, bruto: 604889602 },
                                            { name: '2026/ene', neto: 546003919, bruto: 637688455 },
                                            { name: '2026/feb', neto: 9779983, bruto: 11369384 },
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                            <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${(value / 1000000).toFixed(0)} mill.`} />
                                            <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                                            <Legend />
                                            <Bar dataKey="neto" name="Gastado Neto" fill="#38bdf8" />
                                            <Bar dataKey="bruto" name="Gastado Bruto" fill="#0ea5e9" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Bottom Left Table: Detalle */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Detalle</h3>
                                        <table className="w-full text-sm text-right text-slate-600 dark:text-slate-400">
                                            <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                                <tr>
                                                    <th className="px-4 py-2 text-left">Año Mes</th>
                                                    <th className="px-4 py-2">Gastado Neto</th>
                                                    <th className="px-4 py-2">Gastado Bruto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[
                                                    { name: '2025/ago', neto: 2132720, bruto: 2132720 },
                                                    { name: '2025/sep', neto: 82509193, bruto: 94959820 },
                                                    { name: '2025/oct', neto: 223858439, bruto: 259053156 },
                                                    { name: '2025/nov', neto: 210094005, bruto: 238648239 },
                                                    { name: '2025/dic', neto: 526368506, bruto: 604889602 },
                                                    { name: '2026/ene', neto: 546003919, bruto: 637688455 },
                                                    { name: '2026/feb', neto: 9779983, bruto: 11369384 },
                                                ].map((row, idx) => (
                                                    <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                                                        <td className="px-4 py-2 text-left font-medium text-slate-900 dark:text-white">{row.name}</td>
                                                        <td className="px-4 py-2">{row.neto.toLocaleString()}</td>
                                                        <td className="px-4 py-2">{row.bruto.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-primary text-white font-bold">
                                                <tr>
                                                    <td className="px-4 py-2 text-left">Total</td>
                                                    <td className="px-4 py-2">1.600.746.765</td>
                                                    <td className="px-4 py-2">1.848.741.375</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    {/* Bottom Right Pie Chart */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Distribución por Grupo</h3>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: '1 - MATERIALES', value: 19.46, fill: '#f0f9ff' },
                                                        { name: '2 - SUBCONTRATOS', value: 20.52, fill: '#bae6fd' },
                                                        { name: '3 - OBRAS COMPLEMENTARIAS', value: 6.32, fill: '#7dd3fc' },
                                                        { name: '4 - GASTOS GENERALES', value: 13.43, fill: '#38bdf8' },
                                                        { name: '5 - HONORARIOS', value: 0.9, fill: '#0ea5e9' },
                                                        { name: '6 - URBANIZACIONES', value: 9.0, fill: '#0284c7' },
                                                        { name: '8 - MANO DE OBRA', value: 15.68, fill: '#0369a1' },
                                                        { name: '9 - MENSAJES Y UTILIDAD', value: 14.69, fill: '#1e3a8a' },
                                                    ]}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={80}
                                                    outerRadius={120}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {/* Cells are handled by data fill property */}
                                                </Pie>
                                                <Tooltip />
                                                <Legend layout="vertical" align="right" verticalAlign="middle" />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 4. Detalle de Avance */}
                        {budgetSubTab === 'avance' && (
                            <div className="space-y-6">
                                {/* Top Chart: Avances Stacked Bar */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Avances</h3>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={[
                                            { name: '2025/ago', mat: 0, sub: 0, obr: 0, gas: 0, hon: 0, urb: 0, man: 2132720, men: 0, sin: 0 },
                                            { name: '2025/sep', mat: 12699100, sub: 0, obr: 42462441, gas: 11885608, hon: 1180468, urb: 0, man: 14480779, men: -199200, sin: -3 },
                                            { name: '2025/oct', mat: 68204817, sub: 53828055, obr: 31447852, gas: 25689432, hon: 10682347, urb: 0, man: 31020923, men: 2985012, sin: 1 },
                                            { name: '2025/nov', mat: 55424109, sub: 35179587, obr: 10729542, gas: 50698222, hon: 0, urb: 0, man: 54594245, men: 3468303, sin: -2 },
                                            { name: '2025/dic', mat: 115416449, sub: 98291153, obr: 8949864, gas: 74443384, hon: 1974283, urb: 71710273, man: 91039861, men: 64543466, sin: -227 },
                                            { name: '2026/ene', mat: 59731583, sub: 140884414, obr: 7504078, gas: 49712230, hon: 0, urb: 72344456, man: 57616167, men: 158211136, sin: -146 },
                                            { name: '2026/feb', mat: 88000, sub: 370000, obr: 0, gas: 2526390, hon: 536873, urb: 0, man: 175000, men: 6083720, sin: 0 },
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                            <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${(value / 1000000).toFixed(0)} mill.`} />
                                            <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                                            <Bar dataKey="mat" name="MATERIALES" stackId="a" fill="#f0f9ff" />
                                            <Bar dataKey="sub" name="SUBCONTRATOS" stackId="a" fill="#bae6fd" />
                                            <Bar dataKey="obr" name="OBRAS COMPLEMENTARIAS" stackId="a" fill="#7dd3fc" />
                                            <Bar dataKey="gas" name="GASTOS GENERALES" stackId="a" fill="#38bdf8" />
                                            <Bar dataKey="hon" name="HONORARIOS" stackId="a" fill="#0ea5e9" />
                                            <Bar dataKey="urb" name="URBANIZACIONES" stackId="a" fill="#0284c7" />
                                            <Bar dataKey="man" name="MANO DE OBRA" stackId="a" fill="#0369a1" />
                                            <Bar dataKey="men" name="MENSAJES Y UTILIDAD" stackId="a" fill="#1e3a8a" />
                                            <Bar dataKey="sin" name="SIN CLASIFICAR" stackId="a" fill="#64748b" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Bottom Table: Detalle de Avance Grid */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Detalle de Avance</h3>
                                    <table className="w-full text-xs text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                        <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                            <tr>
                                                <th className="px-4 py-2 text-left bg-slate-50 dark:bg-slate-700 sticky left-0 z-10">Nombre Grupo</th>
                                                <th className="px-4 py-2">2025/ago</th>
                                                <th className="px-4 py-2">2025/sep</th>
                                                <th className="px-4 py-2">2025/oct</th>
                                                <th className="px-4 py-2">2025/nov</th>
                                                <th className="px-4 py-2">2025/dic</th>
                                                <th className="px-4 py-2">2026/ene</th>
                                                <th className="px-4 py-2">2026/feb</th>
                                                <th className="px-4 py-2 bg-slate-100 dark:bg-slate-600 font-bold">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Manually constructed rows for grid */}
                                            {[
                                                { name: "1 - MATERIALES", vals: [0, 12699100, 68204817, 55424109, 115416449, 59731583, 88000], total: 311564059 },
                                                { name: "2 - SUBCONTRATOS", vals: [0, 0, 53828055, 35179587, 98291153, 140884414, 370000], total: 328553209 },
                                                { name: "3 - OBRAS COMPLEMENTARIAS", vals: [0, 42462441, 31447852, 10729542, 8949864, 7504078, 0], total: 101093778 },
                                                { name: "4 - GASTOS GENERALES", vals: [0, 11885608, 25689432, 50698222, 74443384, 49712230, 2526390], total: 214955266 },
                                                { name: "5 - HONORARIOS", vals: [0, 1180468, 10682347, 0, 1974283, 0, 536873], total: 14373970 },
                                                { name: "6 - URBANIZACIONES", vals: [0, 0, 0, 0, 71710273, 72344456, 0], total: 144054729 },
                                                { name: "8 - MANO DE OBRA", vals: [2132720, 14480779, 31020923, 54594245, 91039861, 57616167, 175000], total: 251059695 },
                                                { name: "9 - MENSAJES Y UTILIDAD", vals: [0, -199200, 2985012, 3468303, 64543466, 158211136, 6083720], total: 235092437 },
                                                { name: "99 - SIN CLASIFICAR", vals: [0, -3, 1, -2, -227, -146, 0], total: -378 },
                                            ].map((row, idx) => (
                                                <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                                                    <td className="px-4 py-2 text-left font-medium text-slate-900 dark:text-white sticky left-0 bg-white dark:bg-slate-800 z-10">{row.name}</td>
                                                    {row.vals.map((v, i) => (
                                                        <td key={i} className="px-4 py-2">{v !== 0 ? v.toLocaleString() : ''}</td>
                                                    ))}
                                                    <td className="px-4 py-2 bg-primary text-white font-bold">{row.total.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-primary text-white font-bold">
                                            <tr>
                                                <td className="px-4 py-2 text-left sticky left-0 z-10 bg-primary">Total</td>
                                                <td className="px-4 py-2">2.132.720</td>
                                                <td className="px-4 py-2">82.509.193</td>
                                                <td className="px-4 py-2">223.858.439</td>
                                                <td className="px-4 py-2">210.094.005</td>
                                                <td className="px-4 py-2">526.368.506</td>
                                                <td className="px-4 py-2">546.003.919</td>
                                                <td className="px-4 py-2">9.779.983</td>
                                                <td className="px-4 py-2">1.600.746.765</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* --- PLANNING VIEW --- */}
                {activeTab === 'planning' && (
                    <div className="animate-fade-in space-y-6">
                        {/* Planning Sub-Tabs */}
                        <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6">
                            {[
                                { id: 'master', label: 'Planificación Maestra' },
                                { id: 'intermediate', label: 'Planificación Intermedia' },
                                { id: 'weekly', label: 'Planificación Semanal' }
                            ].map(sub => (
                                <button
                                    key={sub.id}
                                    onClick={() => setPlanningSubTab(sub.id as any)}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${planningSubTab === sub.id
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                        }`}
                                >
                                    {sub.label}
                                </button>
                            ))}
                        </div>

                        {planningSubTab === 'master' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <StatCard title="Avance Real" value={`${truncateProgress(planningStats.master.realProgress)}%`} icon="trending_up" color="bg-blue-600" />
                                    <StatCard title="Avance Programado" value={`${truncateProgress(planningStats.master.scheduledProgress)}%`} icon="calendar_today" color="bg-slate-600" />
                                    <StatCard title="Tareas Completadas" value={`${planningStats.master.completedTasks} / ${planningStats.master.totalTasks}`} icon="check_circle" color="bg-green-600" />
                                </div>
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Curva S de Avance (Gantt)</h3>
                                        <div className="flex items-center gap-4">
                                            <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                                <span className="h-2.5 w-2.5 rounded-full bg-slate-300"></span> Progr.
                                            </span>
                                            <span className="flex items-center gap-1.5 text-xs text-slate-500">
                                                <span className="h-2.5 w-2.5 rounded-full bg-blue-600"></span> Real
                                            </span>
                                        </div>
                                    </div>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={planningStats.master.sCurveData}>
                                            <defs>
                                                <linearGradient id="colorPlan" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                            <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `${val}%`} />
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <Tooltip formatter={(value) => `${value}%`} />
                                            <Area type="monotone" dataKey="plan" stroke="#cbd5e1" strokeWidth={2} fill="transparent" name="Avance Planificado" />
                                            <Area type="monotone" dataKey="real" stroke="#1a73e8" strokeWidth={3} fillOpacity={0.1} fill="url(#colorReal)" name="Avance Real" connectNulls />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {planningSubTab === 'intermediate' && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <StatCard title="Restricciones Totales" value={planningStats.intermediate.constraintStats.total} icon="list" color="bg-blue-500" />
                                    <StatCard title="Restricciones Abiertas" value={planningStats.intermediate.constraintStats.open} icon="lock_open" color="bg-orange-500" />
                                    <StatCard title="Restricciones Liberadas" value={planningStats.intermediate.constraintStats.closed} icon="lock" color="bg-green-500" />
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Constraints by Type Bar Chart */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Tipos de Restricciones</h3>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={planningStats.intermediate.constraintTypeData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                                <YAxis fontSize={12} tickLine={false} axisLine={false} />
                                                <Tooltip cursor={{ fill: '#f1f5f9' }} />
                                                <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Cantidad" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Compliance by Responsible Table (Constraints) */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px] overflow-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Gestión por Responsable</h3>
                                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                                <tr>
                                                    <th className="px-4 py-2">Responsable</th>
                                                    <th className="px-4 py-2">Asignadas</th>
                                                    <th className="px-4 py-2">Liberadas</th>
                                                    <th className="px-4 py-2">Pendientes</th>
                                                    <th className="px-4 py-2">% Gestión</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {planningStats.intermediate.constraintResponsibleData.map((row, index) => (
                                                    <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{row.name}</td>
                                                        <td className="px-4 py-2">{row.assigned}</td>
                                                        <td className="px-4 py-2 text-green-600">{row.closed}</td>
                                                        <td className="px-4 py-2 text-orange-600">{row.pending}</td>
                                                        <td className="px-4 py-2 font-bold">{truncateProgress(Number(row.compliance))}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {planningSubTab === 'weekly' && (
                            <div className="space-y-6">
                                {/* Global KPIs */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <StatCard title="% PPC Global" value={`${truncateProgress(planningStats.weekly.globalPPC)}%`} icon="trending_up" color="bg-blue-600" />
                                    <StatCard title="Actividades Asignadas" value={planningStats.weekly.totalAllocations} icon="assignment" color="bg-slate-600" />
                                    <StatCard title="Actividades Cumplidas" value={planningStats.weekly.executedAllocations} icon="check_circle" color="bg-green-600" />
                                    <StatCard title="No Cumplidas" value={planningStats.weekly.failedAllocations} icon="cancel" color="bg-red-600" />
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* CNC Pie Chart */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Causas de No Cumplimiento (CNC)</h3>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={planningStats.weekly.cncData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    label={({ name, percentage }: any) => `${name} (${percentage}%)`}
                                                >
                                                    {planningStats.weekly.cncData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Compliance by Responsible Table */}
                                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px] overflow-auto">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Cumplimiento por Responsable</h3>
                                        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                                <tr>
                                                    <th className="px-4 py-2">Responsable</th>
                                                    <th className="px-4 py-2">Asignadas</th>
                                                    <th className="px-4 py-2">Cumplidas</th>
                                                    <th className="px-4 py-2">No Cumplidas</th>
                                                    <th className="px-4 py-2">% Cumplimiento</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {planningStats.weekly.responsibleData.map((row, index) => (
                                                    <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{row.name}</td>
                                                        <td className="px-4 py-2">{row.assigned}</td>
                                                        <td className="px-4 py-2 text-green-600">{row.executed}</td>
                                                        <td className="px-4 py-2 text-red-600">{row.failed}</td>
                                                        <td className="px-4 py-2 font-bold">{truncateProgress(Number(row.compliance))}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* --- QUALITY VIEW --- */}
                {activeTab === 'quality' && (
                    <div className="animate-fade-in space-y-6">
                        {/* Filters */}
                        <div className="flex gap-4 mb-4 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Responsable</label>
                                <select
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white"
                                    onChange={(e) => setQualityFilterResponsible(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {responsibles.map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Partida</label>
                                <select
                                    className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white"
                                    onChange={(e) => setQualityFilterTask(e.target.value)}
                                >
                                    <option value="">Todas</option>
                                    {/* Flatten tasks for dropdown - Simple approach */}
                                    {(() => {
                                        const flat: Task[] = [];
                                        const traverse = (t: Task[]) => t.forEach(task => {
                                            flat.push(task);
                                            if (task.children) traverse(task.children);
                                        });
                                        traverse(tasks);
                                        return flat.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ));
                                    })()}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <StatCard title="Total Protocolos" value={qualityStats.total} icon="assignment" color="bg-indigo-500" />
                            <StatCard title="Aprobados" value={qualityStats.data.find(d => d.name === 'Cerrado')?.value || 0} icon="verified" color="bg-green-500" />
                            <StatCard title="Pendientes" value={qualityStats.data.find(d => d.name === 'Abierto')?.value || 0} icon="pending" color="bg-amber-500" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Chart */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Estado de Protocolos</h3>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={qualityStats.data}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {qualityStats.data.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.name === 'Abierto' ? '#f59e0b' : entry.name === 'Cerrado' ? '#10b981' : '#ef4444'} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Compliance Table */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px] overflow-auto">
                                <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Cumplimiento por Responsable</h3>
                                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                        <tr>
                                            <th className="px-4 py-2">Responsable</th>
                                            <th className="px-4 py-2">Asignados</th>
                                            <th className="px-4 py-2">Cerrados</th>
                                            <th className="px-4 py-2">Abiertos</th>
                                            <th className="px-4 py-2">% Cumplimiento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {qualityStats.complianceData.map((row, index) => (
                                            <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                                <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{row.name}</td>
                                                <td className="px-4 py-2">{row.assigned}</td>
                                                <td className="px-4 py-2 text-green-600">{row.closed}</td>
                                                <td className="px-4 py-2 text-orange-600">{row.open}</td>
                                                <td className="px-4 py-2 font-bold">{truncateProgress(Number(row.compliance))}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- SUBCONTRACTS VIEW --- */}
                {activeTab === 'subcontracts' && (
                    <div className="animate-fade-in space-y-6">
                        {/* Sub-Tabs */}
                        <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6 font-medium">
                            <button
                                onClick={() => setSubcontractsSubTab('contratos')}
                                className={`px-6 py-3 border-b-2 transition-colors ${subcontractsSubTab === 'contratos'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                Contratos
                            </button>
                            <button
                                onClick={() => setSubcontractsSubTab('estados_pago')}
                                className={`px-6 py-3 border-b-2 transition-colors ${subcontractsSubTab === 'estados_pago'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                Estados de Pago
                            </button>
                        </div>

                        {/* 1. Contratos Tab */}
                        {subcontractsSubTab === 'contratos' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Left Table: List of Contracts */}
                                <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Listado de Subcontratos</h3>
                                    <table className="w-full text-sm text-left text-slate-600 dark:text-slate-400">
                                        <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200">
                                            <tr>
                                                <th className="px-4 py-3">Subcontrato</th>
                                                <th className="px-4 py-3">Inicio</th>
                                                <th className="px-4 py-3">Término</th>
                                                <th className="px-4 py-3 text-right">Monto</th>
                                                <th className="px-4 py-3 text-center">Anexos</th>
                                                <th className="px-4 py-3 text-center">Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subcontractStats.contractsBreakdown.length > 0 ? (
                                                subcontractStats.contractsBreakdown.map((c, idx) => (
                                                    <tr key={idx} className="bg-white border-b dark:bg-slate-800 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
                                                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{c.subcontractor}</td>
                                                        <td className="px-4 py-3">{c.startDate}</td>
                                                        <td className="px-4 py-3">{c.endDate}</td>
                                                        <td className="px-4 py-3 text-right font-medium">${c.amount.toLocaleString()}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="bg-slate-100 text-slate-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-slate-700 dark:text-slate-300">
                                                                {c.attachments}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.status === 'Activo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                                                c.status === 'Borrador' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                                                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                                                }`}>
                                                                {c.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                                                        No hay contratos registrados
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Right Pie Chart: Amount Distribution */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[400px]">
                                    <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Distribución por Monto</h3>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={subcontractStats.contractsPieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {subcontractStats.contractsPieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                                            <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: '11px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* 2. Estados de Pago Tab */}
                        {subcontractsSubTab === 'estados_pago' && (
                            <div className="space-y-6">
                                {/* Filter */}
                                <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row gap-4 items-center">
                                    <div className="w-full md:w-1/3">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Filtrar por Subcontrato</label>
                                        <select
                                            value={subcontractFilter}
                                            onChange={(e) => setSubcontractFilter(e.target.value)}
                                            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary"
                                        >
                                            <option value="">Todos</option>
                                            {subcontractStats.epBreakdown.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1 flex gap-4 justify-end">
                                        <div className="text-right">
                                            <p className="text-sm text-slate-500">Total EPs</p>
                                            <p className="text-xl font-bold text-slate-900 dark:text-white">
                                                {subcontractStats.epBreakdown
                                                    .filter(item => !subcontractFilter || item.id === subcontractFilter)
                                                    .reduce((acc, curr) => acc + curr.totalEPs, 0)}
                                            </p>
                                        </div>
                                        <div className="text-right border-l border-slate-200 dark:border-slate-700 pl-4">
                                            <p className="text-sm text-slate-500">Monto Total Pagado</p>
                                            <p className="text-xl font-bold text-emerald-600">
                                                ${subcontractStats.epBreakdown
                                                    .filter(item => !subcontractFilter || item.id === subcontractFilter)
                                                    .reduce((acc, curr) => acc + curr.totalPaid, 0).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Left Table: List of Subcontractors/EPs */}
                                    <div className="lg:col-span-1 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto h-[500px]">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">Resumen por Subcontratista</h3>
                                        <table className="w-full text-sm text-left text-slate-600 dark:text-slate-400">
                                            <thead className="text-xs text-slate-900 uppercase bg-slate-50 dark:bg-slate-700 dark:text-slate-200 sticky top-0">
                                                <tr>
                                                    <th className="px-4 py-2">Subcontratista</th>
                                                    <th className="px-4 py-2 text-center">N° EPs</th>
                                                    <th className="px-4 py-2 text-right">Monto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {subcontractStats.epBreakdown
                                                    .filter(item => !subcontractFilter || item.id === subcontractFilter)
                                                    .map((item, idx) => (
                                                        <tr key={idx}
                                                            className={`border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${subcontractFilter === item.id ? 'bg-blue-50 dark:bg-slate-700' : 'bg-white dark:bg-slate-800'}`}
                                                            onClick={() => setSubcontractFilter(item.id === subcontractFilter ? '' : item.id)}
                                                        >
                                                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{item.name}</td>
                                                            <td className="px-4 py-3 text-center">{item.totalEPs}</td>
                                                            <td className="px-4 py-3 text-right font-medium text-emerald-600">${item.totalPaid.toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Right Bar Chart: EPs Detail */}
                                    <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-[500px]">
                                        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-white">
                                            {subcontractFilter ? 'Detalle de Estados de Pago' : 'Monto Total por Subcontratista'}
                                        </h3>
                                        <ResponsiveContainer width="100%" height="100%">
                                            {subcontractFilter ? (
                                                <BarChart data={subcontractStats.epBreakdown.find(i => i.id === subcontractFilter)?.eps || []}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis dataKey="period" fontSize={12} tickLine={false} axisLine={false} />
                                                    <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                                                    <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Monto']} />
                                                    <Bar dataKey="amount" name="Monto" fill="#0ea5e9" radius={[4, 4, 0, 0]} barSize={50} />
                                                </BarChart>
                                            ) : (
                                                <BarChart data={subcontractStats.epBreakdown}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
                                                    <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`} />
                                                    <Tooltip formatter={(value: number) => [`$${value.toLocaleString()}`, 'Total Pagado']} />
                                                    <Bar dataKey="totalPaid" name="Total Pagado" fill="#0ea5e9" radius={[4, 4, 0, 0]} barSize={40} />
                                                </BarChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
